import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { useLazyGetPresentacionRarPDFbyIdQuery } from '../../../services/presentacionesRarApi';
import type { CuitPresentacionSvcc } from '../hooks/usePresentacionesSVCC';

export type RarDelEstablecimiento = CuitPresentacionSvcc['rarsDelEstablecimiento'][number];
export type NominaSvcc = CuitPresentacionSvcc['nomina'];

interface RarDelEstablecimientoDialogProps {
    abierto: boolean;
    onClose: () => void;
    /** CUIT de la fila, para el título: el modal se abre desde una celda sin contexto. */
    cuit: string;
    /**
     * Período de la fila. Decide qué RAR son "del período" y contra cuál se
     * compara la nómina. null cuando la fila no tiene período: se muestran
     * todos sin distinguir.
     */
    anioPeriodo: number | null;
    /** Los RAR presentados de los establecimientos de la fila, del más nuevo al más viejo. */
    rars: RarDelEstablecimiento[];
    /** Lo que va a viajar contra lo que el informe declara. */
    nomina: NominaSvcc;
}

const formatearCuit = (cuit: string) =>
    /^\d{11}$/.test(cuit) ? `${cuit.slice(0, 2)}-${cuit.slice(2, 10)}-${cuit.slice(10)}` : cuit;

/**
 * La fecha del RAR es un día, no un instante: viaja como medianoche UTC y en
 * hora local (UTC-3) se corre al día anterior. Se lee en UTC, igual que en la
 * celda de período.
 */
const formatearFecha = (fecha: string | null): string | null => {
    if (!fecha) return null;
    const valor = new Date(fecha);
    return Number.isNaN(valor.getTime())
        ? null
        : valor.toLocaleDateString('es-AR', {
              timeZone: 'UTC',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
          });
};

const formatearCuil = (cuil: string) =>
    /^\d{11}$/.test(cuil) ? `${cuil.slice(0, 2)}-${cuil.slice(2, 10)}-${cuil.slice(10)}` : cuil;

const etiquetaRar = (rar: RarDelEstablecimiento, vigenteAlCierre = false) => {
    const fecha = formatearFecha(rar.fechaPresentacion);
    const trabajadores = `${rar.trabajadores} ${rar.trabajadores === 1 ? 'trabajador' : 'trabajadores'}`;
    return [
        `RAR ${rar.id}`,
        fecha,
        rar.establecimiento,
        trabajadores,
        vigenteAlCierre ? 'vigente al cierre' : null
    ]
        .filter(Boolean)
        .join(' · ');
};

/** Un RAR por establecimiento: el primero de cada uno en una lista que viene del más nuevo al más viejo. */
const masRecientePorEstablecimiento = (rars: RarDelEstablecimiento[]) =>
    rars.filter(
        (rar, i) =>
            rars.findIndex((otro) => (otro.establecimiento ?? otro.id) === (rar.establecimiento ?? rar.id)) === i
    );

/**
 * Qué RAR describe la nómina del período YYYY, y cuáles no.
 *
 * El SVCC del período YYYY declara a los expuestos durante el año calendario
 * YYYY. Un RAR es la nómina el día que se presentó, vigente hasta que otro lo
 * reemplaza. Entonces:
 *
 * - `vigentesAlCierre`: el más reciente hasta el 31/12/YYYY de cada
 *   establecimiento. Es el que describe la nómina al cierre del año declarado
 *   —regla del cliente, 16/09/2026— y contra el que se compara lo que viaja.
 * - `delPeriodo`: los presentados durante YYYY, más el vigente al cierre si es
 *   anterior (no hubo RAR en el año y el de antes siguió rigiendo).
 * - `anteriores`: los de antes de YYYY que otro reemplazó antes del cierre.
 * - `posteriores`: los de después del 31/12. No describen YYYY: uno de enero
 *   de YYYY+1 muestra a quien ingresó en enero. Aunque se haya presentado
 *   "para" esa DDJJ — hubo una versión que los contaba y por eso se sacó.
 * - `sinRarAlCierre`: hay RAR, pero ninguno hasta el 31/12: sólo posteriores.
 *   Caso real del cliente nuevo que presenta su primer RAR en enero y declara
 *   el año anterior.
 *
 * Con `anioPeriodo` null (fila sin período, o RAR sin `anio` de un backend
 * anterior) no hay con qué distinguir: todo es "del período" y se compara con
 * el más reciente de cada establecimiento.
 */
export const clasificarRars = (rars: RarDelEstablecimiento[], anioPeriodo: number | null) => {
    if (anioPeriodo == null) {
        return {
            delPeriodo: rars,
            vigentesAlCierre: masRecientePorEstablecimiento(rars),
            anteriores: [] as RarDelEstablecimiento[],
            posteriores: [] as RarDelEstablecimiento[],
            sinRarAlCierre: false
        };
    }
    const anioDe = (rar: RarDelEstablecimiento) => rar.anio ?? Number.NaN;
    const hastaElCierre = rars.filter((rar) => anioDe(rar) <= anioPeriodo);
    const vigentesAlCierre = masRecientePorEstablecimiento(hastaElCierre);
    const delAnio = rars.filter((rar) => anioDe(rar) === anioPeriodo);
    const delPeriodo = rars.filter((rar) => delAnio.includes(rar) || vigentesAlCierre.includes(rar));
    return {
        delPeriodo,
        vigentesAlCierre,
        anteriores: hastaElCierre.filter((rar) => !delPeriodo.includes(rar)),
        posteriores: rars.filter((rar) => anioDe(rar) > anioPeriodo),
        sinRarAlCierre: rars.length > 0 && hastaElCierre.length === 0
    };
};

/**
 * Los RAR presentados del establecimiento, con el PDF de cada uno, y arriba de
 * todo lo que la SRT va a recibir como nómina.
 *
 * Este modal reemplaza a uno que decía "el RAR del que salió la nómina". Esa
 * frase no tiene respuesta en el modelo: el informe SVCC apunta a la nómina
 * VIVA del establecimiento (`empleados_nomina`), no a un RAR, y el Anexo III se
 * arma leyendo esa misma tabla viva al momento de enviar. Un RAR es un
 * snapshot de esa nómina en una fecha; útil para mirar, no "el origen".
 *
 * **Qué RAR es del período.** Ver `clasificarRars`: los del año de la fila y el
 * vigente al cierre van primero y son contra los que se compara la nómina; los
 * anteriores reemplazados y los posteriores quedan aparte. Antes se comparaba
 * contra el más nuevo aunque la fila fuera de dos años atrás, y el modal
 * mezclaba los RAR de todos los años bajo el año de la fila.
 *
 * Por eso el resumen de arriba es lo primero: **cuántos trabajadores van a
 * viajar** (misma condición que usa el envío) contra **cuántos declara el
 * informe**. Cuando no coinciden —o cuando el envío saldría vacío— es lo que
 * el operador tiene que saber antes que cualquier RAR.
 *
 * **El PDF se embebe, no se descarga.** El backend devuelve una URL firmada de
 * S3 (`presentaciones/:id/archivo?idTipo=2`, una hora de vigencia) y el archivo
 * está subido con `ContentType: application/pdf` y sin `Content-Disposition`,
 * así que el navegador lo renderiza. El `iframe` es una navegación, no un
 * `fetch`: no pasa por CORS, que sí bloquea leer ese mismo archivo por JS.
 *
 * Aun así "Abrir en una pestaña" está SIEMPRE, no sólo ante un error: un
 * `iframe` cross-origin no avisa de forma confiable cuando no pudo mostrar algo
 * —queda en blanco y listo—, así que la salida tiene que estar a la vista antes
 * de que haga falta.
 */
const RarDelEstablecimientoDialog = ({
    abierto,
    onClose,
    cuit,
    anioPeriodo,
    rars,
    nomina
}: RarDelEstablecimientoDialogProps) => {
    const [seleccionado, setSeleccionado] = useState<number | null>(null);
    const [indiceArchivo, setIndiceArchivo] = useState(0);
    const [pedirPdf, { data, isFetching, isError }] = useLazyGetPresentacionRarPDFbyIdQuery();

    /*
     * Sin `anio` (backend anterior a este cambio) no se filtra: todos serían
     * "posteriores" o ninguno, por un dato que no llegó.
     */
    const conAnio = rars.length > 0 && rars.every((rar) => typeof rar.anio === 'number');
    const filtrarPorPeriodo = anioPeriodo != null && conAnio;
    const { delPeriodo, vigentesAlCierre, anteriores, posteriores, sinRarAlCierre } = clasificarRars(
        rars,
        filtrarPorPeriodo ? anioPeriodo : null
    );
    // Sólo hay RAR del año de la fila, o hubo que ir al vigente de antes.
    const soloVigenteAnterior =
        filtrarPorPeriodo && delPeriodo.length > 0 && delPeriodo.every((rar) => rar.anio !== anioPeriodo);

    /*
     * Se preselecciona el vigente al cierre —el que describe el año—: un modal
     * que abre vacío obliga a un click para ver lo que se vino a ver, y abrir
     * en el RAR de otro año obliga a dos. Sin ninguno hasta el cierre, el más
     * nuevo que haya: al menos se ve algo.
     */
    const idInicial = vigentesAlCierre[0]?.id ?? posteriores[0]?.id ?? rars[0]?.id ?? null;
    useEffect(() => {
        if (!abierto) return;
        setSeleccionado(idInicial);
        setIndiceArchivo(0);
    }, [abierto, idInicial]);

    useEffect(() => {
        if (!abierto || seleccionado == null) return;
        setIndiceArchivo(0);
        // `typeId` lo pide el tipo del endpoint pero no se usa: adentro fuerza
        // `idTipo: 2`, que es el PDF.
        pedirPdf({ idPresentacion: seleccionado, typeId: 2 });
    }, [abierto, seleccionado, pedirPdf]);

    const urls = data?.data?.presignedUrls ?? [];
    const url = urls[indiceArchivo];

    const sinTrabajadores = nomina.trabajadoresParaEnviar === 0;
    const ajenos = nomina.expuestosDeOtroCuit ?? [];
    const sinAsignar = nomina.trabajadoresSinEstablecimiento ?? 0;
    /*
     * Tres situaciones, tres acciones. Se separan porque decir "fuera de la
     * nómina" para todo mandaba a revisar una reasignación cuando lo que había
     * era un trabajador de OTRA EMPRESA dentro del informe.
     */
    const sinEstablecimientoExp = nomina.expuestosSinEstablecimiento ?? 0;
    // Lo que queda después de sacar los de otro CUIT y los sin establecimiento:
    // los que sí están en otro establecimiento de este CUIT.
    const reasignados = Math.max(
        0,
        nomina.expuestosFueraDeNomina - ajenos.length - sinEstablecimientoExp
    );
    const hayProblema =
        sinTrabajadores || reasignados > 0 || sinEstablecimientoExp > 0 || ajenos.length > 0;
    const severidad = ajenos.length > 0 ? 'error' : hayProblema ? 'warning' : 'info';

    /*
     * Una sola línea: de qué RAR sale la nómina. Por establecimiento, el vigente
     * al cierre del período; si no hay ninguno hasta el cierre, el más reciente
     * de los posteriores (en SIMP el RAR y el SVCC se cargan seguidos, en el año
     * siguiente al período). Antes se explicaba además el año de los RAR y la
     * diferencia de cantidad contra la nómina de hoy, y el texto no se entendía.
     */
    const rarRecientePorEstablecimiento =
        sinRarAlCierre && posteriores.length > 0
            ? masRecientePorEstablecimiento(posteriores)
            : vigentesAlCierre;
    const describirRars = () => {
        if (rarRecientePorEstablecimiento.length === 1) {
            const [rar] = rarRecientePorEstablecimiento;
            const fecha = formatearFecha(rar.fechaPresentacion);
            return `del RAR ${rar.id}${fecha ? ` (${fecha})` : ''}`;
        }
        const ids = rarRecientePorEstablecimiento.map((rar) => rar.id);
        return `de los RAR ${ids.slice(0, -1).join(', ')} y ${ids[ids.length - 1]}`;
    };

    return (
        <Dialog
            open={abierto}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { height: '90vh' } }}
            data-testid="rar-establecimiento-dialog"
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Typography variant="h6" component="span" sx={{ display: 'block' }}>
                    Nómina y RAR del establecimiento
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {`CUIT ${formatearCuit(cuit)}`}
                </Typography>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2 }}
            >
                {/*
                  * Lo que va a viajar, primero. Es lo que decide si tiene sentido
                  * enviar, y no se puede leer de ningún RAR.
                  */}
                <Alert
                    severity={severidad}
                    variant="outlined"
                    data-testid="rar-establecimiento-nomina"
                >
                    <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
                        {`${nomina.trabajadoresParaEnviar} ${
                            nomina.trabajadoresParaEnviar === 1 ? 'trabajador' : 'trabajadores'
                        } para enviar · ${nomina.expuestosDeclarados} ${
                            nomina.expuestosDeclarados === 1
                                ? 'expuesto declarado'
                                : 'expuestos declarados'
                        }`}
                    </Typography>
                    {/*
                      * Orden: lo que bloquea, después lo que no viaja, y el RAR al
                      * final como contexto. Cada cosa en una línea y sin la frase
                      * general "la SRT recibe la nómina actual" cuando hay un
                      * problema concreto: seis líneas para llegar al "es de otro
                      * CUIT" no se leían.
                      */}
                    {sinTrabajadores && (
                        <Typography
                            variant="body2"
                            component="div"
                            data-testid="rar-establecimiento-sin-trabajadores"
                        >
                            {sinAsignar > 0
                                ? `Bloqueado: ningún trabajador asignado a este establecimiento. Hay ${sinAsignar} en la nómina sin establecimiento: asignalos.`
                                : 'Bloqueado: el establecimiento no tiene nómina. Importala antes de presentar.'}
                        </Typography>
                    )}
                    {ajenos.length > 0 && (
                        <Box data-testid="rar-establecimiento-otro-cuit">
                            <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
                                {ajenos.length === 1
                                    ? 'Expuesto de OTRO CUIT, no se puede declarar acá. Revisá el RAR del que se cargó:'
                                    : `${ajenos.length} expuestos de OTRO CUIT, no se pueden declarar acá. Revisá el RAR del que se cargaron:`}
                            </Typography>
                            {ajenos.map((a) => (
                                <Typography key={a.cuil} variant="body2" component="div">
                                    {`${a.apellido}${a.nombre ? `, ${a.nombre}` : ''} · CUIL ${formatearCuil(
                                        a.cuil
                                    )} → ${a.empresa ?? 'otra empresa'}${
                                        a.cuit ? ` (CUIT ${formatearCuit(a.cuit)})` : ''
                                    }`}
                                </Typography>
                            ))}
                        </Box>
                    )}
                    {sinEstablecimientoExp > 0 && (
                        <Typography
                            variant="body2"
                            component="div"
                            data-testid="rar-establecimiento-expuestos-sin-est"
                        >
                            {sinEstablecimientoExp === 1
                                ? 'El expuesto declarado no tiene establecimiento en la nómina: no viaja. Asignalo a este establecimiento.'
                                : `${sinEstablecimientoExp} expuestos declarados no tienen establecimiento en la nómina: no viajan. Asignalos a este establecimiento.`}
                        </Typography>
                    )}
                    {reasignados > 0 && (
                        <Typography
                            variant="body2"
                            component="div"
                            data-testid="rar-establecimiento-reasignados"
                        >
                            {reasignados === 1
                                ? 'Un expuesto declarado está hoy en otro establecimiento de este CUIT: no viaja. Revisá a cuál pertenece.'
                                : `${reasignados} expuestos declarados están hoy en otro establecimiento de este CUIT: no viajan. Revisá a cuál pertenecen.`}
                        </Typography>
                    )}
                    {rarRecientePorEstablecimiento.length > 0 && (
                        <Typography
                            variant="body2"
                            component="div"
                            data-testid="rar-establecimiento-explicacion"
                            sx={{ color: 'text.secondary' }}
                        >
                            {`Nómina ${describirRars()}.`}
                        </Typography>
                    )}
                </Alert>

                {rars.length === 0 ? (
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                        data-testid="rar-establecimiento-sin-rar"
                    >
                        El establecimiento no tiene ningún RAR presentado.
                    </Typography>
                ) : (
                    <>
                        {/*
                          * Los que describen el período primero, el resto aparte y
                          * en gris. Sin período en la fila (o sin `anio` en los RAR)
                          * es una sola lista sin título: no hay con qué distinguir.
                          */}
                        {filtrarPorPeriodo && delPeriodo.length > 0 && (
                            <Typography
                                variant="subtitle2"
                                data-testid="rar-establecimiento-titulo-periodo"
                            >
                                {soloVigenteAnterior
                                    ? `RAR vigente al cierre de ${anioPeriodo}`
                                    : `RAR del período ${anioPeriodo}`}
                            </Typography>
                        )}
                        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                            {delPeriodo.map((rar) => (
                                <Button
                                    key={rar.id}
                                    size="small"
                                    variant={rar.id === seleccionado ? 'contained' : 'outlined'}
                                    onClick={() => setSeleccionado(rar.id)}
                                    aria-label={`Ver RAR ${rar.id}`}
                                    data-testid={`rar-establecimiento-rar-${rar.id}`}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {etiquetaRar(
                                        rar,
                                        filtrarPorPeriodo && vigentesAlCierre.includes(rar)
                                    )}
                                </Button>
                            ))}
                        </Box>
                        {[
                            { clave: 'anteriores', titulo: 'RAR anteriores, reemplazados antes del cierre', lista: anteriores },
                            { clave: 'posteriores', titulo: 'RAR posteriores al período', lista: posteriores }
                        ]
                            .filter((grupo) => grupo.lista.length > 0)
                            .map((grupo) => (
                                <Box key={grupo.clave} data-testid={`rar-establecimiento-${grupo.clave}`}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ color: 'text.secondary', mb: 1 }}
                                    >
                                        {grupo.titulo}
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                        {grupo.lista.map((rar) => (
                                            <Button
                                                key={rar.id}
                                                size="small"
                                                color="inherit"
                                                variant={rar.id === seleccionado ? 'contained' : 'outlined'}
                                                onClick={() => setSeleccionado(rar.id)}
                                                aria-label={`Ver RAR ${rar.id}`}
                                                data-testid={`rar-establecimiento-rar-${rar.id}`}
                                                sx={{ textTransform: 'none', color: 'text.secondary' }}
                                            >
                                                {etiquetaRar(rar)}
                                            </Button>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                    </>
                )}

                {/* Una presentación puede tener el PDF partido en varios archivos. */}
                {urls.length > 1 && (
                    <Box display="flex" gap={1} alignItems="center">
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {`El RAR ${seleccionado} tiene ${urls.length} archivos:`}
                        </Typography>
                        {urls.map((_, i) => (
                            <Button
                                key={i}
                                size="small"
                                variant={i === indiceArchivo ? 'contained' : 'text'}
                                onClick={() => setIndiceArchivo(i)}
                                sx={{ textTransform: 'none', minWidth: 0 }}
                            >
                                {`Parte ${i + 1}`}
                            </Button>
                        ))}
                    </Box>
                )}

                {rars.length > 0 && (
                    <Box
                        flex={1}
                        minHeight={0}
                        border={1}
                        borderColor="divider"
                        borderRadius={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        overflow="hidden"
                    >
                        {isFetching && <CircularProgress />}

                        {!isFetching && isError && (
                            <Typography
                                variant="body2"
                                sx={{ color: 'error.main', p: 2, textAlign: 'center' }}
                                data-testid="rar-establecimiento-error"
                            >
                                No se pudo obtener el archivo del RAR. Puede que todavía no se
                                haya generado.
                            </Typography>
                        )}

                        {!isFetching && !isError && !url && (
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary', p: 2, textAlign: 'center' }}
                                data-testid="rar-establecimiento-sin-archivo"
                            >
                                Este RAR no tiene un archivo generado para mostrar.
                            </Typography>
                        )}

                        {!isFetching && !isError && url && (
                            <iframe
                                key={url}
                                src={url}
                                title={`RAR ${seleccionado}`}
                                data-testid="rar-establecimiento-visor"
                                style={{ width: '100%', height: '100%', border: 0 }}
                            />
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    startIcon={<OpenInNewIcon />}
                    disabled={!url}
                    onClick={() => url && window.open(url, '_blank', 'noopener')}
                    sx={{ textTransform: 'none', mr: 'auto' }}
                >
                    Abrir en una pestaña
                </Button>
                <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none' }}>
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RarDelEstablecimientoDialog;
